import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import ConnectedLayout from 'layouts/connectedLayout';
import PrivateLayout from 'layouts/pivateLayout';

// guards
import GuestGuard from 'guards/GuestGuard';
import AuthGuard from 'guards/AuthGuard';
import PrivateGuard from 'guards/PrivateGuard';
// config
import { PATH_AFTER_LOGIN } from 'config';
// components
import LoadingScreen from 'components/LoadingScreen';
import RoleBasedGuard from 'guards/RoleBasedGuard';

import { PATH_DASHBOARD, PATH_PRIVATE } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {

  return useRoutes([
    {
      path: '',
      element: <GuestGuard>
        <Login />
      </GuestGuard>,
    },
    {

    },

    {
      path: 'directAccess',
      element: (
        <PrivateGuard>
          <PrivateLayout />
        </PrivateGuard>
      ),
      children: [
        {
          path: '',
          children: [
            /* detail demande */
            { path: `${PATH_PRIVATE.demandes.list}/:ipp`, element: <ExamList /> },
            { path: `${PATH_PRIVATE.demandes.list}/:ipp/:demandeCode`, element: <ExamList /> },
          ],
        },
        // Add the fallback route to handle "route not found"
        {
          path: '*', // wildcard to match any path that doesn't exist
          element: <RoleBasedGuard /> // replace with your "NotFound" component
        }
      ]
    },
    // activity Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <ConnectedLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: '',
          children: [
            { element: <Navigate to={PATH_DASHBOARD.consultation.list} replace />, index: true },
            /* consulte demandes */
            { path: `${PATH_DASHBOARD.demandes.list}`, element: <PatientList /> },
            /* detail demande */
            { path: `${PATH_DASHBOARD.demandes.list}/:ipp`, element: <ExamList /> },
            { path: `${PATH_DASHBOARD.demandes.list}/:ipp/:demandeCode`, element: <ExamList /> },

            /* consultation history */
            { path: `${PATH_DASHBOARD.consultation.list}`, element: <ConsultationHistoryList /> },
            /* ccount */
            { path: `${PATH_DASHBOARD.account.edit}`, element: <AccountPage /> },
              /* préférences  */
            { path: `${PATH_DASHBOARD.preferences.edit}`, element: <PreferencesPage /> },
            /* message */
            { path: PATH_DASHBOARD.message.list, element: <MessageList /> },
            /* user */
            { path: PATH_DASHBOARD.user.list, element: <UserList /> },
            { path: PATH_DASHBOARD.user.add, element: <UserList /> },
            { path: `${PATH_DASHBOARD.user.add}/:id`, element: <UserList /> },
            /* delegation */
            { path: PATH_DASHBOARD.delegation.list, element: <DelegationList /> },
            { path: `${PATH_DASHBOARD.delegation.add}`, element: <DelegationModal /> },
            { path: `${PATH_DASHBOARD.delegation.add}/:userId`, element: <DelegationModal /> },
            { path: `${PATH_DASHBOARD.delegation.add}/:userId/:patientId`, element: <DelegationModal /> },
            { path: `${PATH_DASHBOARD.delegation.edit}/:actorCode/:userId/:patientId`, element: <DelegationModal /> },
            { path: `${PATH_DASHBOARD.delegation.edit}/:actorCode/:userId/:datedeb/:datefin`, element: <DelegationModal /> },

            /* Association */
            { path: PATH_DASHBOARD.association.list, element: <AssociationList /> },
            { path: `${PATH_DASHBOARD.association.add}`, element: <AssociationModel /> },
            { path: `${PATH_DASHBOARD.association.add}/:id`, element: <AssociationModel /> },
            /* Fusion */
            { path: PATH_DASHBOARD.fusion.list, element: <FusionList /> },
            /* Download */
            { path: PATH_DASHBOARD.download.list, element: <DownloadList /> },
            /* Setting */
            { path: PATH_DASHBOARD.setting.edit, element: <SettingPage /> },
            /* Profile */
            { path: PATH_DASHBOARD.profile.list, element: <ProfileList /> },
            { path: `${PATH_DASHBOARD.profile.add}`, element: <ProfileList /> },
            { path: `${PATH_DASHBOARD.profile.add}/:id`, element: <ProfileList /> },
            /* acteur */
            { path: PATH_DASHBOARD.actor.list, element: <ActorList /> },
            { path: PATH_DASHBOARD.actor.add, element: <ActorList /> },
            { path: `${PATH_DASHBOARD.actor.add}/:id`, element: <ActorList /> },
            /* groups */
            { path: PATH_DASHBOARD.groups.list, element: <GroupsList /> },
            { path: PATH_DASHBOARD.groups.add, element: <GroupsList /> },
            { path: `${PATH_DASHBOARD.groups.add}/:id`, element: <GroupsList /> },
            /* dictionary */
            { path: PATH_DASHBOARD.dictionary.list, element: <DictionaryList /> },
            { path: `${PATH_DASHBOARD.dictionary.add}/:id/:dictionaryType`, element: <DictionaryList /> },
            /* home */
            { path: PATH_DASHBOARD.home.app, element: <Home /> },

          ]
        }
      ]
    }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('pages/auth/Login')));
// Dashboard
const PatientList = Loadable(lazy(() => import('pages/patient/PatientList')));
const MessageList = Loadable(lazy(() => import('pages/message/MessageList')));;
const ConsultationHistoryList = Loadable(lazy(() => import('pages/consultation/ConsultationHistoryList')));;

const ExamList = Loadable(lazy(() => import('pages/exam/ExamList')));
const UserList = Loadable(lazy(() => import('pages/user/UserList')));
const AccountPage = Loadable(lazy(() => import('pages/account/Account')));
const PreferencesPage = Loadable(lazy(() => import('pages/preferences/PreferencesPages')));;

const DelegationList = Loadable(lazy(() => import('pages/delegation/DelegationList')));
const DelegationModal = Loadable(lazy(() => import('pages/delegation/DelegationModel')));

const AssociationList = Loadable(lazy(() => import('pages/association/AssociationList')));
const AssociationModel = Loadable(lazy(() => import('pages/association/AssociationModel')));

const FusionList = Loadable(lazy(() => import('pages/fusion/FusionList')));
const DownloadList = Loadable(lazy(() => import('pages/download/DownloadList')));

const SettingPage = Loadable(lazy(() => import('pages/setting/Setting')));

const ProfileList = Loadable(lazy(() => import('pages/profile/ProfileList')));

const ActorList = Loadable(lazy(() => import('pages/actor/ActorList')));

const GroupsList = Loadable(lazy(() => import('pages/groups/GroupsList')));

const DictionaryList = Loadable(lazy(() => import('pages/dictionary/DictionaryList')));
const Home = Loadable(lazy(() => import('pages/home/Home')));
